import type { IConversation } from '~/entities/container/conversation/interfaces/conversation'

import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'

type MESSAGE_ID = string

/**
 * Used to handle marks of conversations. This locks a conversation once active and hence uses that reference
 * to show separators and badges
 */
export const useTemporaryMarks = createGlobalState(() => {
  /**
   * Has the mark been set yet. This is needed so that we don't lock the conversation too soon
   */
  const firstMarkSet = ref(false)

  /**
   * The map of conversation ids and their marks
   */
  const tempMarks = reactive<Map<SUBJECT_6W_URI, MESSAGE_ID>>(new Map())

  /**
   * URI of the current conversation. The marks will not update for this conversation
   */
  const currentConversationUri = ref<SUBJECT_6W_URI>('')

  /**
   * Function to set the first mark as true.
   * Useful if markAsRead is called but we don't intend to set the mark.
   * This way it will unblock the wait in "setCurrentConversationUri" function
   */
  const setFirstMarkSet = () => {
    firstMarkSet.value = true
  }

  /**
   * Function that sets the marks in a global reactive state
   *
   * @param conversation The conversation object
   */
  const setTempMarks = (conversation: Pick<IConversation, 'subject_uri' | 'mark'>) => {
    if (!conversation.mark || conversation.subject_uri === currentConversationUri.value)
      return

    tempMarks.set(conversation.subject_uri, conversation.mark.message_id)
    setFirstMarkSet()
  }

  /**
   * Function that sets a value to the current conversation ref
   *
   * @param params the parameters
   * @param params.uri The URI to set
   * @param params.reset Reset the marks of previously set conversation
   */
  const setCurrentConversationUri = async ({ uri, reset = false }: {
    /**
     * The URI to set
     */
    uri: SUBJECT_6W_URI
    /**
     * Reset the marks of previously set conversation
     */
    reset: boolean
  }) => {
    // Wait until atleast one mark is set
    await until(firstMarkSet)
      .toBe(true, {
        timeout: 25000,
      })

    if (reset)
      tempMarks.set(currentConversationUri.value, '')

    currentConversationUri.value = uri
  }

  return {
    tempMarks,
    setTempMarks,
    setFirstMarkSet,
    setCurrentConversationUri,
  }
})
